import { useState } from 'react';

interface Props {
	onConfirm: (comment: string) => Promise<void>;
	emptyCommentError: string;
	/**
	 * Identifies what is being rejected. The flow belongs to one subject, so when this
	 * changes the box closes and the comment is dropped — without it a comment typed for
	 * one subject follows the reviewer to the next one and can be confirmed against it.
	 */
	resetKey?: string;
}

/**
 * The decline-with-a-comment flow: the decline action reveals a comment box, and the same
 * action confirms it. An empty comment is reported inline rather than by disabling the
 * button. Shared by KYC review and bank account review.
 */
export const useRejectionState = ({ onConfirm, emptyCommentError, resetKey }: Props) => {
	const [isRejecting, setIsRejecting] = useState(false);
	const [comment, setComment] = useState('');
	const [commentError, setCommentError] = useState('');
	const [renderedFor, setRenderedFor] = useState(resetKey);

	// Adjusting state during render is React's documented way to drop state when the
	// subject changes; an effect would let one render paint the previous subject's comment.
	if (resetKey !== renderedFor) {
		setRenderedFor(resetKey);
		setIsRejecting(false);
		setComment('');
		setCommentError('');
	}

	const reset = () => {
		setIsRejecting(false);
		setComment('');
		setCommentError('');
	};

	const handleCommentChange = (next: string) => {
		if (commentError) {
			setCommentError('');
		}
		setComment(next);
	};

	const handleConfirmReject = async () => {
		// Trimmed, because a blank comment is refused server-side: without this the
		// reviewer gets a generic request-failed toast instead of the inline message.
		if (!comment.trim()) {
			setCommentError(emptyCommentError);
			return;
		}
		await onConfirm(comment);
		reset();
	};

	const handleCancelReject = () => {
		reset();
	};

	const handleDecline = async () => {
		if (!isRejecting) {
			setIsRejecting(true);
			return;
		}
		await handleConfirmReject();
	};

	return {
		isRejecting,
		comment,
		commentError,
		handleCommentChange,
		handleConfirmReject,
		handleCancelReject,
		handleDecline,
		reset,
	};
};
